import {
  BACKGROUND_STYLE_VALUES,
  EXPRESSION_VALUES,
  resolveLayout,
  SHAPE_VALUES,
} from '@usespaceui/squishmoji';

import {
  imaiCharacterBody,
  isImaiCharacterColor,
} from '@/lib/imai-character/catalog';

/** A member's IMAI as stored; any field may be missing. */
export type YourImaiPersonaInput = {
  name?: string;
  shape?: string;
  expression?: string;
  backgroundStyle?: string;
  colorSeed?: string;
  /** An IMAI character body id; when set it replaces the blob look. */
  character?: string;
  /** The character's body and accent colours. */
  characterColors?: string[];
};

/** A picked IMAI character: a body and its two colours. */
export type YourImaiCharacterLook = {
  body: string;
  colors: readonly [string, string];
};

export type YourImaiShape = (typeof SHAPE_VALUES)[number];
export type YourImaiExpression = (typeof EXPRESSION_VALUES)[number];
export type YourImaiBackgroundStyle = (typeof BACKGROUND_STYLE_VALUES)[number];

/** A persona with every field resolved, ready to render. */
export type YourImaiPersona = {
  name: string;
  shape: YourImaiShape;
  expression: YourImaiExpression;
  backgroundStyle: YourImaiBackgroundStyle;
  colorSeed: string;
  /** Set when the member picked an IMAI character instead of a blob. */
  character: YourImaiCharacterLook | null;
};

/**
 * Colour choices: squishmoji picks a palette from the seed, so each seed here
 * was chosen for a distinct body colour. The first is the default.
 */
export const YOUR_IMAI_COLOR_SEEDS = [
  'imai-22',
  'imai-67',
  'imai-190',
  'imai-5',
  'imai-43',
  'imai-55',
  'imai-4',
  'imai-25',
  'imai-24',
  'imai-50',
  'imai-6',
  'imai-15',
] as const;

/** Names for the colour choices, in the order above. */
const COLOR_NAMES: Record<(typeof YOUR_IMAI_COLOR_SEEDS)[number], string> = {
  'imai-22': 'Lavender',
  'imai-67': 'Blue',
  'imai-190': 'Yellow',
  'imai-5': 'Magenta',
  'imai-43': 'Lime',
  'imai-55': 'Pink',
  'imai-4': 'Mint',
  'imai-25': 'Orange',
  'imai-24': 'Violet',
  'imai-50': 'Red',
  'imai-6': 'Cyan',
  'imai-15': 'Black',
};

export function yourImaiColorName(
  colorSeed: (typeof YOUR_IMAI_COLOR_SEEDS)[number]
): string {
  return COLOR_NAMES[colorSeed];
}

export const YOUR_IMAI_SHAPES: readonly YourImaiShape[] = SHAPE_VALUES;
export const YOUR_IMAI_EXPRESSIONS: readonly YourImaiExpression[] =
  EXPRESSION_VALUES;

const DEFAULT_LOOK = {
  shape: 'triangle',
  expression: 'happy',
  backgroundStyle: 'solid',
  colorSeed: 'imai-24',
} as const satisfies Omit<YourImaiPersona, 'name' | 'character'>;

function isShape(value: string | undefined): value is YourImaiShape {
  return SHAPE_VALUES.some((shape) => shape === value);
}
function isExpression(value: string | undefined): value is YourImaiExpression {
  return EXPRESSION_VALUES.some((expression) => expression === value);
}
function isBackgroundStyle(
  value: string | undefined
): value is YourImaiBackgroundStyle {
  return BACKGROUND_STYLE_VALUES.some((style) => style === value);
}

/** The stored persona with defaults filled in. */
export function resolveYourImaiPersona(
  input: YourImaiPersonaInput | null | undefined,
  defaultName: string
): YourImaiPersona {
  return {
    name: input?.name?.trim() || defaultName,
    shape: isShape(input?.shape) ? input.shape : DEFAULT_LOOK.shape,
    expression: isExpression(input?.expression)
      ? input.expression
      : DEFAULT_LOOK.expression,
    backgroundStyle: isBackgroundStyle(input?.backgroundStyle)
      ? input.backgroundStyle
      : DEFAULT_LOOK.backgroundStyle,
    colorSeed: input?.colorSeed?.trim() || DEFAULT_LOOK.colorSeed,
    character: resolveCharacter(input?.character, input?.characterColors),
  };
}

/** A stored character look, if its body still exists; unknown colours fall back to the body's own. */
export function resolveCharacter(
  body: string | undefined,
  colors: readonly string[] | undefined
): YourImaiCharacterLook | null {
  const found = body ? imaiCharacterBody(body) : null;
  if (!body || !found) return null;
  const [color, accent] = colors ?? [];
  return {
    body,
    colors:
      color &&
      accent &&
      isImaiCharacterColor(color) &&
      isImaiCharacterColor(accent)
        ? [color, accent]
        : found.colors,
  };
}

/** The look fields to save for a persona: the blob parts, plus its character when one is picked. */
export function yourImaiLookToSave(persona: YourImaiPersona) {
  return {
    shape: persona.shape,
    expression: persona.expression,
    backgroundStyle: persona.backgroundStyle,
    colorSeed: persona.colorSeed,
    ...(persona.character
      ? {
          character: persona.character.body,
          characterColors: [...persona.character.colors],
        }
      : {}),
  };
}

/**
 * The persona as shown: IMAI's passing mood face, while it has one, over
 * the saved face. Everything else stays as saved.
 */
export function withYourImaiMood(
  persona: YourImaiPersona,
  moodExpression: string | null | undefined
): YourImaiPersona {
  const mood = moodExpression ?? undefined;
  return isExpression(mood) && mood !== persona.expression
    ? { ...persona, expression: mood }
    : persona;
}

/** Colours of the member's own bubbles: their blob's body colour. */
export type YourImaiBubbleColors = { background: string; foreground: string };

const bubbleColorsBySeed = new Map<string, YourImaiBubbleColors>();

type Rgb = readonly [number, number, number];

function parseHex(hex: string): Rgb | null {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match?.[1]) return null;
  const value = Number.parseInt(match[1], 16);
  return [
    ((value >> 16) & 0xff) / 255,
    ((value >> 8) & 0xff) / 255,
    (value & 0xff) / 255,
  ];
}

/** APCA screen luminance, with its soft clamp for near-black. */
function apcaLuminance([r, g, b]: Rgb): number {
  const y = 0.2126729 * r ** 2.4 + 0.7151522 * g ** 2.4 + 0.072175 * b ** 2.4;
  return y < 0.022 ? y + (0.022 - y) ** 1.414 : y;
}

/**
 * APCA lightness contrast (|Lc|) of text on a background. Unlike the WCAG
 * ratio it matches how text on saturated mid tones reads: white on a bright
 * blue beats black, which WCAG gets backwards.
 */
function apcaContrast(text: Rgb, background: Rgb): number {
  const yText = apcaLuminance(text);
  const yBackground = apcaLuminance(background);
  if (yBackground > yText) {
    const sapc = (yBackground ** 0.56 - yText ** 0.57) * 1.14;
    return sapc < 0.1 ? 0 : (sapc - 0.027) * 100;
  }
  const sapc = (yBackground ** 0.65 - yText ** 0.62) * 1.14;
  return sapc > -0.1 ? 0 : Math.abs((sapc + 0.027) * 100);
}

const WHITE: Rgb = [1, 1, 1];
/**
 * White text on the bubble reaches at least this APCA contrast: comfortable
 * body-text reading (Lc 75+ at 15px), not the bare legibility floor.
 */
const TARGET_CONTRAST = 78;
/** The bubble is always at least this much deeper than the blob itself. */
const MIN_DARKEN = 0.86;
const DARKEN_STEP = 0.96;

function rgbCss([r, g, b]: Rgb): string {
  return `rgb(${Math.round(r * 255)} ${Math.round(g * 255)} ${Math.round(b * 255)})`;
}

function scaled([r, g, b]: Rgb, factor: number): Rgb {
  return [r * factor, g * factor, b * factor];
}

/**
 * The member's bubble: a deeper shade of their blob's colour (same hue),
 * darkened until white text on it reads comfortably. The colour depends
 * only on the colour seed.
 */
export function yourImaiBubbleColors(colorSeed: string): YourImaiBubbleColors {
  const cached = bubbleColorsBySeed.get(colorSeed);
  if (cached) return cached;
  const body = parseHex(resolveLayout(colorSeed).colors.body);
  let shade: Rgb = body ? scaled(body, MIN_DARKEN) : [0.2, 0.22, 0.26];
  while (
    apcaContrast(WHITE, shade) < TARGET_CONTRAST &&
    shade.some((c) => c > 0.02)
  )
    shade = scaled(shade, DARKEN_STEP);
  const colors = { background: rgbCss(shade), foreground: 'rgb(255 255 255)' };
  bubbleColorsBySeed.set(colorSeed, colors);
  return colors;
}
